import React from "react";
import PropTypes from "prop-types";
import { utils } from "../../../utils/utils";

// Material UI
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";

// Icons
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import FileCopyIcon from "@mui/icons-material/FileCopy";
import BlockIcon from "@mui/icons-material/Block";
import CheckIcon from "@mui/icons-material/Check";

// Components
import IconCheck from "../../iconCheck";
import useStyles from "./styles";
import ChipState from "../../ChipState";
import { Checkbox } from "@mui/material";

const Item = ({
  keyColumns,
  item,
  onUpdate,
  onDelete,
  onView,
  onCopy,
  onSelect,
  isSelected,
  align,
  showActiveInactive,
  onShowUpdate,
  onShowDelete,
  customCells,
  customFields,
}) => {
  const { classes } = useStyles({ align });
  // eslint-disable-next-line
  const dateRegex = /\d{4}\-(0[1-9]|1[012])\-(0[1-9]|[12][0-9]|3[01])/g;

  const showUpdate = onShowUpdate ? onShowUpdate(item) : true;

  const ValueComponent = ({ value }) => {
    // Si es un vinculo (probablemente el link de una imagen en un blob)
    if (typeof value === "string" && value.startsWith("http"))
      return (
        <div className={classes.cardIconWrapper}>
          <img src={value} alt="item" className={classes.cardIcon}></img>
        </div>
      );

    // Si es un array de valores
    if (Array.isArray(value) && value.length > 0)
      return (
        <Typography variant="body1" component="p" className={classes.cardTitle}>
          {value.join()}
        </Typography>
      );

    // Si es una DateTime
    if (String(value).match(dateRegex) !== null)
      return (
        <Typography variant="body1" component="p" className={classes.cardTitle}>
          {utils.formatDate(value)}
        </Typography>
      );

    // Si es un boolean
    if (typeof value === "boolean")
      return (
        <Typography variant="body1" component="p" className={classes.cardTitle}>
          <IconCheck value={value} />
        </Typography>
      );

    // Default
    return (
      <Typography variant="body1" component="p" className={classes.cardTitle}>
        {value}
      </Typography>
    );
  };

  return (
    <Card className={classes.card}>
      <CardContent className={classes.cardContent}>
        <Grid container alignItems="center" spacing={2}>
          {onSelect && (
            <Checkbox
              color="primary"
              checked={isSelected(item)}
              onChange={(e, checked) => onSelect(checked, item)}
              style={{ marginRight: "20px" }}
            />
          )}
          {keyColumns &&
            keyColumns.map((key, i) =>
              typeof key !== "object" ? (
                <Grid item xs key={i} size='grow'>
                  {
                    // Si el dato es un estado, muestra un chip
                    key === "state" || key === "stateToShow"
                      ? (
                        <ChipState state={item.state} label={item[key]} />
                      )
                      : (
                        <ValueComponent value={item[key]} />
                      )
                  }
                </Grid>
              ) : (
                <Grid item xs key={i}>
                  <ValueComponent value={item[key.key1][key.key2]} />
                </Grid>
              )
            )}
          {(!!onView ||
            !!onCopy ||
            !!onDelete ||
            !!onUpdate ||
            !!customCells) && (
              <Grid
                item
                xs={
                  !!onView + !!onCopy + !!onDelete + !!onUpdate + !!customCells <
                    3
                    ? 1
                    : 2
                }
                className={classes.actionPanel}
              >
                {onView && (
                  <Tooltip title="Ver" aria-label="ver">
                    <IconButton onClick={() => onView(item)} size="large">
                      <VisibilityIcon />
                    </IconButton>
                  </Tooltip>
                )}
                {onCopy && (
                  <Tooltip title="Copiar" aria-label="copiar">
                    <IconButton onClick={() => onCopy(item)} size="large">
                      <FileCopyIcon className={classes.copy} />
                    </IconButton>
                  </Tooltip>
                )}
                {showUpdate && onUpdate && (
                  <Tooltip title="Editar" aria-label="editar">
                    <IconButton color="primary" onClick={() => onUpdate(item)} size="large">
                      <EditIcon />
                    </IconButton>
                  </Tooltip>
                )}
                {onDelete && !showActiveInactive
                  ? (
                    <Tooltip title="Eliminar" aria-label="eliminar">
                      <IconButton onClick={() => onDelete(item)} size="large">
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  )
                  : showActiveInactive
                    ? (
                      item.active
                        ? (
                          <Tooltip title="Dejar inactivo" aria-label="Dejar inactivo">
                            <IconButton onClick={() => onDelete(item)} size="large">
                              <BlockIcon style={{ color: "#f44336" }} />
                            </IconButton>
                          </Tooltip>
                        )
                        : (
                          <Tooltip title="Dejar activo" aria-label="Dejar activo">
                            <IconButton onClick={() => onDelete(item)} size="large">
                              <CheckIcon style={{ color: "rgb(76, 175, 80)" }} />
                            </IconButton>
                          </Tooltip>
                        )
                    )
                    : null}
                {customCells && customCells(item)}
              </Grid>
            )}
        </Grid>
      </CardContent>
    </Card>
  );
};

Item.defaultProps = {
  align: "center",
};

Item.prototype = {
  align: PropTypes.string,
  item: PropTypes.object,
  onView: PropTypes.func,
  onUpdate: PropTypes.func,
  onDelete: PropTypes.func,
  onShowUpdate: PropTypes.func,
  onShowDelete: PropTypes.func,
  customCells: PropTypes.func,
  onCopy: PropTypes.func,
};

export default Item;
